import { Link } from "react-router-dom";
import { Shield, UserCheck, Download, Trash2, Lock, Globe, Mail, FileCheck } from "lucide-react";

const GDPRCompliance = () => {
  const lastUpdated = "January 21, 2026";
  return <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black backdrop-blur-md border-b border-white/10">
        
      </header>

      <main className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Title Section */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-10 h-10 text-purple-500" />
            <h1 className="text-4xl md:text-5xl font-bold">GDPR Compliance</h1>
          </div>
          <div className="flex flex-wrap gap-4 text-sm text-gray-400">
            <span>Last Updated: {lastUpdated}</span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <Globe className="w-4 h-4" />
              <span>For EU/EEA Residents</span>
            </span>
          </div>
        </div>

        {/* Introduction */}
        <section className="mb-10">
          <p className="text-lg text-gray-300 leading-relaxed">
            Metro City Trading (Pty) Ltd ("Metro City Groove") is committed to protecting the personal data of 
            individuals in the European Union and European Economic Area in accordance with the General Data 
            Protection Regulation (GDPR). This page explains how we comply with GDPR and your rights under this 
            regulation.
          </p>
        </section>

        {/* Our Commitment */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">Our GDPR Commitment</h2>
          
          <div className="grid gap-4 md:grid-cols-2">
            <div className="p-4 bg-white/5 rounded-xl border border-white/10">
              <div className="flex items-center gap-3 mb-2">
                <Lock className="w-5 h-5 text-purple-400" />
                <h3 className="font-semibold">Lawful Processing</h3>
              </div>
              <p className="text-sm text-gray-400">We only process personal data with a valid legal basis</p>
            </div>
            
            <div className="p-4 bg-white/5 rounded-xl border border-white/10">
              <div className="flex items-center gap-3 mb-2">
                <Shield className="w-5 h-5 text-purple-400" />
                <h3 className="font-semibold">Data Minimization</h3>
              </div>
              <p className="text-sm text-gray-400">We collect only what's necessary for our services</p>
            </div>
            
            <div className="p-4 bg-white/5 rounded-xl border border-white/10">
              <div className="flex items-center gap-3 mb-2">
                <FileCheck className="w-5 h-5 text-purple-400" />
                <h3 className="font-semibold">Transparency</h3>
              </div>
              <p className="text-sm text-gray-400">We clearly explain how we use your data</p>
            </div>
            
            <div className="p-4 bg-white/5 rounded-xl border border-white/10">
              <div className="flex items-center gap-3 mb-2">
                <UserCheck className="w-5 h-5 text-purple-400" />
                <h3 className="font-semibold">Rights Respect</h3>
              </div>
              <p className="text-sm text-gray-400">We honor all your GDPR rights promptly</p>
            </div>
          </div>
        </section>

        {/* Legal Basis */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">Legal Basis for Processing</h2>
          
          <div className="space-y-4 text-gray-300">
            <p>We process your personal data under the following legal bases:</p>
            
            <div className="space-y-4">
              <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                <h4 className="font-semibold text-white mb-2">Contract Performance (Article 6(1)(b))</h4>
                <p className="text-gray-400 text-sm">
                  Processing necessary to provide our Services to you, including account management, 
                  booking services, payment processing, and customer support.
                </p>
              </div>
              
              <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                <h4 className="font-semibold text-white mb-2">Consent (Article 6(1)(a))</h4>
                <p className="text-gray-400 text-sm">
                  Processing based on your explicit consent, such as marketing communications, 
                  non-essential cookies, and optional features. You may withdraw consent at any time.
                </p>
              </div>
              
              <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                <h4 className="font-semibold text-white mb-2">Legitimate Interests (Article 6(1)(f))</h4>
                <p className="text-gray-400 text-sm">
                  Processing necessary for our legitimate business interests, such as fraud prevention, 
                  security, analytics, and service improvement, balanced against your rights.
                </p>
              </div>
              
              <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                <h4 className="font-semibold text-white mb-2">Legal Obligation (Article 6(1)(c))</h4>
                <p className="text-gray-400 text-sm">
                  Processing required to comply with applicable laws, such as tax regulations, 
                  financial reporting, and law enforcement requests.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Your Rights */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-6">Your GDPR Rights</h2>
          
          <div className="space-y-6">
            {/* Right to Access */}
            <div className="p-6 bg-white/5 rounded-xl border border-white/10">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-purple-500/20 rounded-lg">
                  <Download className="w-6 h-6 text-purple-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">Right to Access (Article 15)</h3>
                  <p className="text-gray-400 mb-4">
                    You have the right to request a copy of all personal data we hold about you. We will 
                    provide this information within 30 days of your request.
                  </p>
                  <div className="p-3 bg-white/5 rounded-lg">
                    <p className="text-sm text-gray-300">
                      <strong>What you'll receive:</strong> Account information, profile data, activity logs, 
                      transaction history, communications, and any other personal data we process.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right to Rectification */}
            <div className="p-6 bg-white/5 rounded-xl border border-white/10">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-500/20 rounded-lg">
                  <UserCheck className="w-6 h-6 text-blue-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">Right to Rectification (Article 16)</h3>
                  <p className="text-gray-400 mb-4">
                    You can request correction of inaccurate personal data or completion of incomplete data.
                  </p>
                  <div className="p-3 bg-white/5 rounded-lg">
                    <p className="text-sm text-gray-300">
                      <strong>How to update:</strong> Most data can be updated directly in your account settings. 
                      For other corrections, contact our support team.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right to Erasure */}
            <div className="p-6 bg-white/5 rounded-xl border border-white/10">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-red-500/20 rounded-lg">
                  <Trash2 className="w-6 h-6 text-red-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">Right to Erasure (Article 17)</h3>
                  <p className="text-gray-400 mb-4">
                    Also known as the "right to be forgotten," you can request deletion of your personal data 
                    in certain circumstances.
                  </p>
                  <div className="p-3 bg-white/5 rounded-lg">
                    <p className="text-sm text-gray-300 mb-2">
                      <strong>When it applies:</strong>
                    </p>
                    <ul className="text-sm text-gray-400 list-disc list-inside space-y-1">
                      <li>Data is no longer necessary for the original purpose</li>
                      <li>You withdraw consent (where consent was the basis)</li>
                      <li>You object to processing and there are no overriding grounds</li>
                      <li>Data was unlawfully processed</li>
                    </ul>
                  </div>
                  <div className="mt-3 p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                    <p className="text-sm text-yellow-300">
                      <strong>Note:</strong> We may need to retain some data for legal obligations, 
                      dispute resolution, or legitimate business purposes.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right to Restrict Processing */}
            <div className="p-6 bg-white/5 rounded-xl border border-white/10">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-yellow-500/20 rounded-lg">
                  <Lock className="w-6 h-6 text-yellow-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">Right to Restrict Processing (Article 18)</h3>
                  <p className="text-gray-400">
                    You can request that we temporarily stop processing your data while you contest its 
                    accuracy, object to processing, or if you need the data for legal claims.
                  </p>
                </div>
              </div>
            </div>

            {/* Right to Data Portability */}
            <div className="p-6 bg-white/5 rounded-xl border border-white/10">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-green-500/20 rounded-lg">
                  <Download className="w-6 h-6 text-green-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">Right to Data Portability (Article 20)</h3>
                  <p className="text-gray-400">
                    You can request your personal data in a structured, commonly used, and machine-readable 
                    format (such as JSON or CSV) to transfer to another service provider.
                  </p>
                </div>
              </div>
            </div>

            {/* Right to Object */}
            <div className="p-6 bg-white/5 rounded-xl border border-white/10">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-orange-500/20 rounded-lg">
                  <Shield className="w-6 h-6 text-orange-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">Right to Object (Article 21)</h3>
                  <p className="text-gray-400">
                    You can object to processing based on legitimate interests or direct marketing at any time. 
                    For direct marketing, we will stop immediately. For other processing, we will assess your 
                    objection against our grounds for processing.
                  </p>
                </div>
              </div>
            </div>

            {/* Rights Related to Automated Decision-Making */}
            <div className="p-6 bg-white/5 rounded-xl border border-white/10">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-purple-500/20 rounded-lg">
                  <UserCheck className="w-6 h-6 text-purple-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">Rights Related to Automated Decision-Making (Article 22)</h3>
                  <p className="text-gray-400">
                    You have the right not to be subject to decisions based solely on automated processing 
                    that significantly affect you. Where we use automated decision-making, you can request 
                    human intervention, express your views, and contest the decision.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How to Exercise Your Rights */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">How to Exercise Your Rights</h2>
          
          <div className="space-y-4 text-gray-300">
            <p>To exercise any of your GDPR rights:</p>
            
            <div className="grid gap-4 md:grid-cols-2">
              <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                <h4 className="font-semibold text-white mb-2">1. Submit a Request</h4>
                <p className="text-sm text-gray-400">
                  Email <a href="mailto:privacy@metrocitygroove.online" className="text-purple-400 hover:underline">privacy@metrocitygroove.online</a> with 
                  "GDPR Request" in the subject line.
                </p>
              </div>
              
              <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                <h4 className="font-semibold text-white mb-2">2. Verify Identity</h4>
                <p className="text-sm text-gray-400">
                  We may ask you to verify your identity to protect your data from unauthorized access.
                </p>
              </div>
              
              <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                <h4 className="font-semibold text-white mb-2">3. Receive Response</h4>
                <p className="text-sm text-gray-400">
                  We will respond within 30 days. Complex requests may take up to 90 days with notice.
                </p>
              </div>
              
              <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                <h4 className="font-semibold text-white mb-2">4. No Fee</h4>
                <p className="text-sm text-gray-400">
                  Requests are free unless manifestly unfounded or excessive.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* International Transfers */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">International Data Transfers</h2>
          
          <div className="space-y-4 text-gray-300">
            <p>
              As Metro City Groove is based in South Africa, your data may be transferred outside the EU/EEA. 
              We ensure appropriate safeguards for such transfers:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-400">
              <li><strong>Standard Contractual Clauses (SCCs):</strong> We use EU-approved SCCs with all data recipients outside the EU/EEA</li>
              <li><strong>Adequacy Decisions:</strong> Where applicable, we transfer data to countries with EU adequacy decisions</li>
              <li><strong>Data Processing Agreements:</strong> All service providers sign binding agreements ensuring GDPR-equivalent protection</li>
              <li><strong>Technical Measures:</strong> Encryption and security measures protect data during transfer</li>
            </ul>
          </div>
        </section>

        {/* Data Retention */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">Data Retention</h2>
          
          <div className="space-y-4 text-gray-300">
            <p>We retain your personal data only as long as necessary:</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left border-b border-white/10">
                  <tr>
                    <th className="py-3 pr-4 text-gray-400">Data Category</th>
                    <th className="py-3 text-gray-400">Retention Period</th>
                  </tr>
                </thead>
                <tbody className="text-gray-300">
                  <tr className="border-b border-white/5">
                    <td className="py-3 pr-4">Account data</td>
                    <td className="py-3">Duration of account + 30 days after deletion</td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-3 pr-4">Transaction records</td>
                    <td className="py-3">7 years (legal requirement)</td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-3 pr-4">Marketing consent</td>
                    <td className="py-3">Until consent is withdrawn</td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-3 pr-4">Support communications</td>
                    <td className="py-3">2 years from resolution</td>
                  </tr>
                  <tr>
                    <td className="py-3 pr-4">Analytics data</td>
                    <td className="py-3">26 months (anonymized thereafter)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Data Protection Officer */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">Data Protection Contact</h2>
          
          <div className="p-6 bg-white/5 rounded-xl border border-white/10 space-y-4">
            <p className="text-gray-300">
              While we are not required to appoint a Data Protection Officer under GDPR, we have designated 
              a privacy team to handle all data protection matters:
            </p>
            <div className="space-y-2">
              <p><strong className="text-white">Metro City Groove Privacy Team</strong></p>
              <p className="text-gray-400">Email: <a href="mailto:privacy@metrocitygroove.online" className="text-purple-400 hover:underline">privacy@metrocitygroove.online</a></p>
            </div>
          </div>
        </section>

        {/* Complaints */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">Right to Lodge a Complaint</h2>
          
          <div className="space-y-4 text-gray-300">
            <p>
              If you believe we have not handled your personal data properly, you have the right to lodge a 
              complaint with a supervisory authority. You may contact:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-400">
              <li>The data protection authority in your EU/EEA country of residence</li>
              <li>The data protection authority where the alleged infringement occurred</li>
              <li>The Information Regulator in South Africa for matters related to POPIA</li>
            </ul>
            <p className="text-gray-400">
              However, we encourage you to contact us first so we can address your concerns directly.
            </p>
          </div>
        </section>

        {/* Contact Section */}
        <section className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <Mail className="w-6 h-6 text-purple-400" />
            <h2 className="text-2xl font-semibold">Contact Us</h2>
          </div>
          
          <div className="p-6 bg-purple-500/10 border border-purple-500/20 rounded-xl space-y-4">
            <p className="text-gray-300">
              For any questions about GDPR compliance or to exercise your rights:
            </p>
            <div className="space-y-2">
              <p><strong className="text-white">Metro City Trading (Pty) Ltd</strong></p>
              <p className="text-gray-400">Privacy & Data Protection Team</p>
              <p className="text-gray-400">Email: <a href="mailto:privacy@metrocitygroove.online" className="text-purple-400 hover:underline">privacy@metrocitygroove.online</a></p>
              <p className="text-gray-400">Subject Line: GDPR Inquiry</p>
            </div>
          </div>
        </section>

        {/* Related Links */}
        <section className="mt-12 p-6 bg-white/5 rounded-xl border border-white/10">
          <h3 className="text-lg font-semibold mb-4">Related Policies</h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/privacy" className="text-purple-400 hover:underline">Privacy Policy</Link>
            <Link to="/terms" className="text-purple-400 hover:underline">Terms of Service</Link>
            <Link to="/cookies" className="text-purple-400 hover:underline">Cookie Policy</Link>
          </div>
        </section>
      </main>
    </div>;
};
export default GDPRCompliance;